import { useState, type FormEvent } from 'react'
import { Box, TextField, Button, Typography, Alert, Link as MuiLink } from '@mui/material'
import { Link } from 'react-router-dom'
import { type APIResponse, DASHBOARD_AUTH_PATHS } from '@yourownmetric/shared'

import { apiSlice } from '@/apis/apiSlice'

const forgotPasswordApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        forgotPassword: builder.mutation<{ message: string }, { email: string }>({
            query: body => ({
                url: '/auth/forgot-password',
                method: 'POST',
                body,
            }),
            transformResponse: (res: APIResponse<{ message: string }>) => res.data!,
        }),
    }),
})

const { useForgotPasswordMutation } = forgotPasswordApi

export function ForgotPasswordPage() {
    const [email, setEmail] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [forgotPassword, { isLoading, error }] = useForgotPasswordMutation()

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            await forgotPassword({ email }).unwrap()
            setSubmitted(true)
        } catch {
            // error handled by RTK Query
        }
    }

    if (submitted) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxWidth: 400,
                    mx: 'auto',
                    textAlign: 'center',
                }}
            >
                <Typography variant="h4">Check your email</Typography>
                <Typography variant="body1" color="text.secondary">
                    If an account exists with <strong>{email}</strong>, we&apos;ve sent a password
                    reset link. Please check your inbox.
                </Typography>
                <MuiLink component={Link} to={DASHBOARD_AUTH_PATHS.login} variant="body2">
                    Back to Sign In
                </MuiLink>
            </Box>
        )
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400, mx: 'auto' }}
        >
            <Typography variant="h4" textAlign="center">
                Forgot Password
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
                Enter your email and we&apos;ll send you a reset link
            </Typography>

            {error && <Alert severity="error">Something went wrong. Please try again.</Alert>}

            <TextField
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                fullWidth
            />

            <Button type="submit" variant="contained" size="large" disabled={isLoading} fullWidth>
                {isLoading ? 'Sending…' : 'Send Reset Link'}
            </Button>

            <Typography variant="body2" textAlign="center">
                <MuiLink component={Link} to={DASHBOARD_AUTH_PATHS.login}>
                    Back to Sign In
                </MuiLink>
            </Typography>
        </Box>
    )
}
